import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useBetaFeatureState } from '@/entities/beta-feature';
import { DASHBOARD_PRESET_ROUTE_PATH, useDashboardPresets, usePinnedDashboardPresetRoutes } from '@/entities/settings';
import { useAppNavigate } from '@/shared/lib';

/**
 * Держит согласованными раздел, открытый адресом, и применённый дашборд-пресет.
 *
 * Заход в раздел закреплённого пресета применяет его к странице. Адрес, которому не отвечает ни
 * один закреплённый пресет (открепление, удаление, переименование, ссылка в никуда), уводит на
 * страницу дашбордов. Сам раздел «Дашборды» закреплённого пресета не держит: он там снимается —
 * иначе раздел показывал бы дашборд соседнего раздела. Исключение — закрепление применённого
 * пресета: свой адрес у него появился только что, и страница переходит в его раздел с сохранением
 * применения, а не теряет дашборд, которым пользователь занят. Незакреплённый пресет своего адреса
 * не имеет и на странице дашбордов остаётся применённым.
 *
 * При выключенной бета-функции пользовательских дашбордов закреплённых разделов нет вовсе:
 * сохранённая ссылка на раздел пресета уводит на страницу дашбордов. Пока состояние функции
 * не получено, раздел ничего не решает: неотличимое от выключенной функции ожидание увело бы
 * с адреса закреплённого пресета при каждой перезагрузке страницы и сняло бы применённый.
 * Так же раздел ждёт системные и совместные дашборды с сервера: до них закреплённого серверного в
 * списке ещё нет, а слаги одноимённых дашбордов ещё не разведены. Отказ запроса серверных дашбордов
 * раздел не разрешает: решать по неполному списку нельзя, поэтому страница показывает применённый
 * дашборд и с адреса не уводит. Применённый серверный дашборд, которого в полученном списке нет
 * (системный удалён, совместный удалён владельцем, пользователя убрали из получателей), снимается —
 * страница возвращается к стандартному, — а его фильтры, сменённые пользователем, забываются.
 *
 * Возвращает обработчик смены применённого пресета (закреплённый открывается по своему адресу,
 * незакреплённый и снятие пресета — по адресу страницы дашбордов) и признак того, что раздел
 * пресета открыт адресом, но показывать его ещё рано.
 */
export const useDashboardPresetSection = () => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useAppNavigate();
  const { isEnabled: isPresetsEnabled, isResolved: isPresetsStateResolved } = useBetaFeatureState('CUSTOM_DASHBOARDS');
  const allPinnedRoutes = usePinnedDashboardPresetRoutes();
  const pinnedRoutes = useMemo(() => (isPresetsEnabled ? allPinnedRoutes : []), [isPresetsEnabled, allPinnedRoutes]);
  const {
    presets,
    isPresetListKnown,
    isPresetListLoading,
    activePresetId,
    activePreset,
    apply,
    clearActive,
    pruneSharedFilters,
  } = useDashboardPresets();
  // Раздел решает только по полному списку дашбордов: до него неизвестно ни состояние бета-функции,
  // ни серверные дашборды
  const isReady = isPresetsStateResolved && isPresetListKnown;

  // Адрес, пресет которого уже применён: применяем только на заходе. Смена применённого пресета
  // при открытом адресе сама уводит с него, но новый адрес приходит отложенно — повторное
  // применение по ещё старому адресу откатило бы выбор пользователя.
  const appliedSlugRef = useRef<string | null>(null);

  // Закрепление пресетов на предыдущем проходе. Отличает закрепление, сделанное пользователем
  // прямо сейчас, от появления уже закреплённого пресета применённым (первая отрисовка,
  // восстановление настроек): там раздел «Дашборды» пресет по-прежнему снимает.
  const previousPinnedStateRef = useRef<Map<string, boolean> | null>(null);

  useEffect(() => {
    if (isPresetListKnown && activePresetId && !activePreset) clearActive();
  }, [isPresetListKnown, activePresetId, activePreset, clearActive]);

  // Серверный дашборд, пропавший из полученного списка, не оставляет за собой фильтров пользователя
  useEffect(() => {
    if (isPresetListKnown) pruneSharedFilters();
  }, [isPresetListKnown, pruneSharedFilters]);

  useEffect(() => {
    // Пока список не полон, отсутствие в нём закреплённого раздела ничего не означает: решать
    // по нему — терять открытый дашборд при перезагрузке страницы.
    if (!isReady) return;

    const previousPinnedState = previousPinnedStateRef.current;
    previousPinnedStateRef.current = new Map(presets.map((preset) => [preset.id, preset.pinned]));

    if (!slug) {
      appliedSlugRef.current = null;
      // Незакреплённый пресет — обычный вид страницы дашбордов, её раздел его не снимает.
      const activeRoute = pinnedRoutes.find((route) => route.preset.id === activePresetId);
      if (!activeRoute) return;
      if (previousPinnedState?.get(activeRoute.preset.id) === false) {
        // Пресет закрепили только что, прямо на странице: он остаётся применённым и переезжает
        // в свой появившийся раздел — снять его закреплением значило бы потерять открытое.
        navigate(DASHBOARD_PRESET_ROUTE_PATH, { slug: activeRoute.slug }, { replace: true });
        return;
      }
      clearActive();
      return;
    }

    const openedRoute = pinnedRoutes.find((route) => route.slug === slug);
    if (!openedRoute) {
      appliedSlugRef.current = null;
      // Слаг считается от названия, поэтому переименование пресета обрывает открытый адрес.
      // Пресет при этом никуда не делся — уводим на его новый адрес, а не со страницы.
      const activeRoute = pinnedRoutes.find((route) => route.preset.id === activePresetId);
      if (activeRoute) {
        navigate(DASHBOARD_PRESET_ROUTE_PATH, { slug: activeRoute.slug }, { replace: true });
        return;
      }
      navigate('/quality-dashboard', undefined, { replace: true });
      return;
    }
    if (appliedSlugRef.current === slug) return;

    appliedSlugRef.current = slug;
    if (activePresetId !== openedRoute.preset.id) {
      apply(openedRoute.preset.id);
    }
  }, [isReady, slug, presets, pinnedRoutes, activePresetId, apply, clearActive, navigate]);

  const onActivePresetChange = useCallback(
    (presetId: string | null) => {
      const targetRoute = presetId ? pinnedRoutes.find((route) => route.preset.id === presetId) : undefined;
      if (targetRoute) {
        navigate(DASHBOARD_PRESET_ROUTE_PATH, { slug: targetRoute.slug });
        return;
      }
      if (slug) navigate('/quality-dashboard');
    },
    [pinnedRoutes, navigate, slug],
  );

  // На адресе раздела до готовности нельзя показать даже стандартный дашборд: он подменил бы
  // собой дашборд, за которым пользователь пришёл. Отказ запроса серверных готовности не даст
  // никогда — ждать его ответа бесконечно незачем.
  const isSectionPending = Boolean(slug) && (!isPresetsStateResolved || isPresetListLoading);

  return { onActivePresetChange, isSectionPending };
};
